<script setup lang="ts">
import { z } from 'zod';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';

definePageMeta({
	layout: 'default',
});

const route = useRoute();
const { requestPasswordReset } = useAuth();
const emailSent = ref(false);
const error = ref<string | null>(null);

const schema = z.object({
	email: z.string().email('Email non valida'),
});

const { handleSubmit, isSubmitting, errors, defineField } = useForm({
	validationSchema: toTypedSchema(schema),
	initialValues: {
		email: (route.query.email as string) ?? '',
	},
});

const [email, emailAttrs] = defineField('email');

const onSubmit = handleSubmit(async (values) => {
	error.value = null;
	const result = await requestPasswordReset(values.email);
	
	if (result.success) {
		emailSent.value = true;
	} else {
		error.value = result.error || 'Errore durante la richiesta di reset password';
	}
});
</script>

<template>
	<div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
		<div class="max-w-md w-full space-y-8">
			<div>
				<h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
					Recupera la tua password
				</h2>
				<p class="mt-2 text-center text-sm text-gray-600">
					Inserisci la tua email e ti invieremo un link per reimpostare la password
				</p>
			</div>

			<div v-if="emailSent" class="rounded-md bg-green-50 p-4">
				<div class="flex">
					<div class="flex-shrink-0">
						<svg
							class="h-5 w-5 text-green-400"
							viewBox="0 0 20 20"
							fill="currentColor"
						>
							<path
								fill-rule="evenodd"
								d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
								clip-rule="evenodd"
							/>
						</svg>
					</div>
					<div class="ml-3">
						<p class="text-sm font-medium text-green-800">
							Email inviata! Controlla la tua casella di posta per il link di reset password.
						</p>
					</div>
				</div>
			</div>

			<form v-else class="mt-8 space-y-6" @submit="onSubmit">
				<div v-if="error" class="rounded-md bg-red-50 p-4">
					<div class="flex">
						<div class="flex-shrink-0">
							<svg
								class="h-5 w-5 text-red-400"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path
									fill-rule="evenodd"
									d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
									clip-rule="evenodd"
								/>
							</svg>
						</div>
						<div class="ml-3">
							<p class="text-sm font-medium text-red-800">{{ error }}</p>
						</div>
					</div>
				</div>

				<div>
					<label for="email" class="block text-sm font-medium text-gray-700">
						Email
					</label>
					<input
						id="email"
						v-model="email"
						v-bind="emailAttrs"
						type="email"
						autocomplete="email"
						class="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
						placeholder="La tua email"
					/>
					<p v-if="errors.email" class="mt-1 text-sm text-red-600">
						{{ errors.email }}
					</p>
				</div>

				<div>
					<button
						type="submit"
						:disabled="isSubmitting"
						class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<span v-if="!isSubmitting">Invia link di reset</span>
						<span v-else>Invio in corso...</span>
					</button>
				</div>

				<div class="text-center">
					<NuxtLink
						to="/login"
						class="font-medium text-primary hover:text-primary/80"
					>
						Torna al login
					</NuxtLink>
				</div>
			</form>
		</div>
	</div>
</template>

